import {
	Icon, IconSize, Input, Popover, PopoverPlacement, Select, Table
} from "@clintonelec/react-storybook";
import type { Column, SelectOption } from "@clintonelec/react-storybook";
import "Components/Dashboards/SupportDashboard/UserTable/UserTable.less";
import { fetchUsersAction, resetUsersAction, selectUsers } from "Data/Redux/Slices/User";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import type { UserResource } from "Interfaces";
import { memo, useMemo, useState } from "react";

const filterSelectOptions: SelectOption[] = [
	{
		label: "Name",
		value: "name"
	},
	{
		label: "Email",
		value: "email"
	}
];

const userTableColumns: Column[] = [
	{
		title: "Username",
		dataIndex: "username",
		key: "username",
		sortable: true
	},
	{
		title: "Status",
		dataIndex: "status",
		key: "status",
		sortable: true
	},
	{
		title: "Email Address",
		dataIndex: "email",
		key: "email",
		sortable: true
	},
	{
		title: "Hostnames",
		dataIndex: "hostnames",
		key: "hostnames",
		sortable: true
	},
	{
		title: "Actions",
		dataIndex: "actions",
		key: "actions",
		sortable: false,
		align: "center"
	}
];

type UserTableProps = {
	onUserClick: (user: UserResource) => void;
};

function UserTable({ onUserClick: handleUserClick }: UserTableProps) {
	const dispatch = useAppDispatch();
	const users = useAppSelector(selectUsers);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ filterOption, setFilterOption ] = useState<string>("name");

	const handleInputEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.currentTarget.value) {
			setLoading(true);

			const filterPayload = event.currentTarget.value
				? { [ `filter[${ filterOption }]` ]: event.currentTarget.value }
				: {};

			dispatch(fetchUsersAction(filterPayload))
				.finally(() => {
					setLoading(false);
				});
		} else {
			dispatch(resetUsersAction());
		}
	};

	const handleFilterSelect = (option: string | null) => {
		setFilterOption(option ?? "");
	};

	const renderPopover = ({ hostnames }: UserResource) => {
		if (hostnames.length === 0) {
			return hostnames.length;
		}

		const popoverContent = (
			<div className="hostname-popover-content">
				{ hostnames.map(({ id, hostname }) => (
					<span key={ id }>{ hostname }.cecdyn.com</span>
				)) }
			</div>
		);

		return (
			<Popover
				content={ popoverContent }
				placement={ PopoverPlacement.RIGHT_START }
				arrow={ false }
			>
				{ hostnames.length }
			</Popover>
		);
	};

	const tableData = useMemo(() => {
		return users?.map(user => ({
			key: user?.id ?? "",
			email: user?.email,
			username: user?.name,
			status: user?.verified ? "Active" : "Disabled",
			hostnames: renderPopover(user),
			actions: (
				<Icon
					className="edit-user-icon"
					name="pencil"
					onClick={ () => handleUserClick(user) }
				/>
			)
		}));
	}, [ users, handleUserClick ]);

	const renderTable = () => {
		const loader = (
			<Icon
				name="circle-notch"
				size={ IconSize.BIGGEST }
				spin
				className="loading-icon"
			/>
		);

		return (
			<Table
				columns={ userTableColumns }
				data={ tableData }
				loader={ loader }
				loading={ loading }
			/>
		);
	};

	return (
		<div className="user-table-container">
			<div className="search-bar">
				<Select
					className="filter-select"
					options={ filterSelectOptions }
					value={ filterOption }
					onSelect={ handleFilterSelect }
					allowClear={ false }
				/>
				<Input
					className="filter-input"
					suffix={ <Icon name="search" /> }
					clearable
					onPressEnter={ handleInputEnter }
				/>
			</div>
			<div className="user-table">
				{ renderTable() }
			</div>
		</div>
	);
}

export default memo(UserTable);
